import React, { FC, useEffect, useState } from "react";
import Box from "@mui/material/Box";
import InputBase from "@mui/material/InputBase";
import SearchIcon from "@mui/icons-material/Search";
import Select from "@/components/Select/Select";
import { useAppTheme } from "@/utils/theme";
import useDebounce from "@/hooks/useDebounce";
import {
  EducationalResourceFilters,
  EducationalResourceStatus,
  EducationalResourceType,
  RESOURCE_STATUSES,
  RESOURCE_STATUS_LABELS,
  RESOURCE_TYPES,
  RESOURCE_TYPE_LABELS,
} from "@/domain/educationalResource.interface";

export const SEARCH_DEBOUNCE_MS = 500;

const TYPE_OPTIONS = RESOURCE_TYPES.map((t) => ({
  key: t,
  value: RESOURCE_TYPE_LABELS[t],
}));

const STATUS_OPTIONS = RESOURCE_STATUSES.map((s) => ({
  key: s,
  value: RESOURCE_STATUS_LABELS[s],
}));

type Props = {
  filtersState: [
    EducationalResourceFilters,
    React.Dispatch<React.SetStateAction<EducationalResourceFilters>>,
  ];
  categoryOptions: { key: string; value: string }[];
  isLoadingCategories: boolean;
};

/** Search (debounced) + Category / Type / Status filters. Every change resets to the first page. */
const EducationalResourcesFilters: FC<Props> = ({
  filtersState,
  categoryOptions,
  isLoadingCategories,
}) => {
  const theme = useAppTheme();
  const [filters, setFilters] = filtersState;
  const [searchInput, setSearchInput] = useState(filters.search);
  const debouncedSearch = useDebounce({
    value: searchInput,
    delay: SEARCH_DEBOUNCE_MS,
  });

  useEffect(() => {
    setFilters((prev) =>
      prev.search === debouncedSearch
        ? prev
        : { ...prev, search: debouncedSearch, pageNumber: 0 }
    );
  }, [debouncedSearch, setFilters]);

  const update = (patch: Partial<EducationalResourceFilters>) =>
    setFilters((prev) => ({ ...prev, ...patch, pageNumber: 0 }));

  return (
    <Box
      sx={{
        display: "flex",
        gap: "0.5rem",
        width: "100%",
        flexDirection: { xs: "column", sm: "column", md: "row" },
      }}
    >
      <Box
        sx={{
          backgroundColor: theme.palette.surface.light2,
          px: 2,
          width: { xs: "100%", sm: "100%" },
        }}
        className="flex rounded-lg py-3 max-w-full items-center justify-between h-14"
      >
        <InputBase
          className="flex-1"
          placeholder="Search by title"
          inputProps={{
            "aria-label": "Search by title",
            "data-testid": "educational-resources-search-input",
          }}
          onChange={(e) => setSearchInput(e.target.value)}
          value={searchInput}
        />
        <SearchIcon className="text-icon-primary" />
      </Box>

      <Box
        sx={{
          display: "flex",
          gap: "0.5rem",
          width: { xs: "100%", sm: "100%" },
          flexDirection: { xs: "column", sm: "row", md: "row" },
        }}
      >
        <Select
          handleChange={(value) => update({ categoryId: value as string })}
          data={categoryOptions}
          value={filters.categoryId}
          label="Category"
          id="educational-resources-category-filter"
          data-testid="educational-resources-category-filter-select"
          isLoading={isLoadingCategories}
          maxWidth={260}
          showClearOption
          fullWidth
        />
        <Select
          handleChange={(value) =>
            update({ type: value as EducationalResourceType | "" })
          }
          data={TYPE_OPTIONS}
          value={filters.type}
          label="Type"
          id="educational-resources-type-filter"
          data-testid="educational-resources-type-filter-select"
          maxWidth={220}
          showClearOption
          fullWidth
        />
        <Select
          handleChange={(value) =>
            update({ status: value as EducationalResourceStatus | "" })
          }
          data={STATUS_OPTIONS}
          value={filters.status}
          label="Status"
          id="educational-resources-status-filter"
          data-testid="educational-resources-status-filter-select"
          maxWidth={220}
          showClearOption
          fullWidth
        />
      </Box>
    </Box>
  );
};

export default EducationalResourcesFilters;
